import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';

const DevelopersSection = () => {
  const developers = [
    {
      name: 'EMAAR',
      logo: 'https://customer-assets.emergentagent.com/job_nava-dubai-wealth/artifacts/tx895ha9_Developer%20EMMAR.png',
      tagline: 'Building Dreams Since 1997',
      description: 'Creator of Burj Khalifa and Dubai Mall, the world\'s most iconic landmarks',
      achievements: ['BURJ KHALIFA', 'EMMAR SOUTH', 'DUBAI MARINA'],
      buildings: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776807665/Burj_khalifa_e8p1hl.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776807665/emaar_south_wviivl.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776807665/dubai_marina_vcfzy7.png',
      ]
    },
    {
      name: 'DAMAC',
      logo: 'https://customer-assets.emergentagent.com/job_nava-dubai-wealth/artifacts/087dst21_Developer%20DAMAC.png',
      tagline: 'Luxury Living Redefined',
      description: 'Premium developments with world-class amenities and design excellence',
      achievements: ['DAMAC HILLS', 'DAMAC LAGOONS', 'DAMAC ISLANDS'],
      buildings: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776807935/damac_hills_judagu.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776807936/DAMAC_LAGOONS_hgiszb.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776807935/DAMAC_ISLANDS_bqbl9v.png',
      ]
    },
    {
      name: 'NAKHEEL',
      logo: 'https://customer-assets.emergentagent.com/job_nava-dubai-wealth/artifacts/0o3vhxmo_DEVELOPER%20%20NAKHEEL.png',
      tagline: 'Shaping Dubai\'s Skyline',
      description: 'Masterminds behind Palm Jumeirah, the world\'s most ambitious project',
      achievements: ['PALM JUMEIRAH', 'JUMEIRAH ISLANDS', 'MUROOJ AL FURJAN'],
      buildings: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808138/palm_ikijrg.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808138/jumera_islands_qb0vuj.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808138/al_furjan_ssh0hy.png',
      ]
    },
    {
      name: 'DUBAI SOUTH',
      logo: 'https://customer-assets.emergentagent.com/job_nava-dubai-wealth/artifacts/50927sz2_DEVELOPER%20DUBAI%20SOUTH.png',
      tagline: 'City of the Future',
      description: 'Next-generation smart city development with sustainable infrastructure',
      achievements: ['EXPO 2020', 'SOUTH BAY', 'PULSE BEACHFRONT'],
      buildings: [
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808346/expo_2020_mdqjer.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808347/south_bay_dm7tkc.png',
        'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776808347/pulse_wrav9e.png',
      ]
    },
  ];

  return (
    <section className="relative py-24 overflow-hidden bg-white" id="developers">
      {/* Background effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-yellow-50 rounded-full blur-3xl opacity-30"
          animate={{
            scale: [1, 1.3, 1],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-gray-800">World-Class</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
              Developer Network
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Partnering with Dubai's most prestigious developers for guaranteed quality and returns
          </p>
        </motion.div>

        {/* Developers showcase */}
        <div className="space-y-12">
          {developers.map((developer, index) => (
            <DeveloperCard key={index} developer={developer} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

const DeveloperCard = ({ developer, index }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % developer.buildings.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [developer.buildings.length]);

  return (
    <motion.div
      className={`flex flex-col ${
        index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
      } gap-8 items-center`}
      initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
    >
      {/* Developer info card */}
      <motion.div
        className="flex-1 relative group"
        whileHover={{ scale: 1.02 }}
      >
        <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
          {/* Logo */}
          <motion.div
            className="mb-6 h-20 flex items-center"
            whileHover={{ scale: 1.05 }}
          >
            <img
              src={developer.logo}
              alt={developer.name}
              className="max-h-full max-w-[200px] object-contain"
            />
          </motion.div>

          <p className="text-yellow-600 text-xl font-semibold mb-4">
            {developer.tagline}
          </p>

          <p className="text-gray-700 text-lg mb-6 leading-relaxed">
            {developer.description}
          </p>

          {/* Achievements */}
          <div className="space-y-3">
            {developer.achievements.map((achievement, idx) => (
              <motion.div
                key={idx}
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 + idx * 0.1 }}
              >
                <CheckCircle2 className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{achievement}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Building images carousel */}
      <motion.div
        className="flex-1 relative h-[400px] lg:h-[500px] w-full"
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: index * 0.2 + 0.2 }}
      >
        <div className="relative h-full bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-xl">
          {/* Cross-dissolve image transition */}
          <AnimatePresence mode="wait">
            <motion.img
              key={currentImageIndex}
              src={developer.buildings[currentImageIndex]}
              alt={`${developer.name} Building ${currentImageIndex + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
            />
          </AnimatePresence>

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Image indicators */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
            {developer.buildings.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentImageIndex
                    ? 'bg-yellow-400 w-8'
                    : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DevelopersSection;
